import { useState } from "react";
import SectionHeader from "@/components/feature/panel/SectionHeader";
import EmptyState from "@/components/feature/panel/EmptyState";
import { useMessages } from "@/hooks/useComms";

export default function Mesajlar() {
  const { data, loading, error, reload, sendMessage, markRead, sending, sendError } = useMessages();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");

  if (loading) return <EmptyState icon="ri-loader-4-line" title="Yükleniyor..." />;
  if (error) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-6 text-center">
        <p className="text-sm text-foreground-600">{error}</p>
        <button
          type="button"
          onClick={reload}
          className="mt-4 inline-flex items-center gap-2 whitespace-nowrap px-4 py-2 rounded-md text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
        >
          <i className="ri-refresh-line" aria-hidden="true"></i>
          Tekrar dene
        </button>
      </div>
    );
  }

  const list = data ?? [];
  const active = list.find((c) => c.id === activeId) ?? list[0] ?? null;

  const openConversation = (id: string) => {
    setActiveId(id);
    markRead(id);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !active || sending) return;
    if (await sendMessage(active.id, newMessage.trim())) setNewMessage("");
  };

  return (
    <div className="space-y-6">
      {sendError && <p role="alert" className="text-red-700">{sendError}</p>}
      <SectionHeader
        title="Mesajlar"
        description="Öğretmenlerle olan görüşmelerini takip et."
      />

      {list.length === 0 ? (
        <EmptyState
          icon="ri-chat-3-line"
          title="Henüz mesaj yok"
          description="Yeni görüşmeler başladıkça burada görünecek."
        />
      ) : (
        <div className="bg-background-50 rounded-lg border border-background-200 overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-[480px]">
          <div className="border-b md:border-b-0 md:border-r border-background-200">
            {list.map((conv) => (
              <button
                key={conv.id}
                type="button"
                onClick={() => openConversation(conv.id)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
                  active?.id === conv.id ? "bg-primary-50" : "hover:bg-background-100"
                }`}
              >
                <div className="relative w-11 h-11 rounded-full overflow-hidden bg-background-100 shrink-0">
                  <img src={conv.avatar} alt={conv.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm text-foreground-950 truncate">{conv.name}</p>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{conv.time}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs text-foreground-500 truncate">{conv.lastMessage}</p>
                    {conv.unread > 0 && (
                      <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-[10px] font-semibold shrink-0">
                        {conv.unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {active ? (
            <div className="flex flex-col">
              <div className="flex items-center gap-3 px-5 py-3.5 border-b border-background-200">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-background-100 shrink-0">
                  <img src={active.avatar} alt={active.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-foreground-950">{active.name}</p>
                  <p className="text-xs text-foreground-500">{active.role}</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-background-50">
                {active.messages.map((msg) => (
                  <div key={msg.id} className={`flex ${msg.isMe ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[75%] rounded-lg px-3.5 py-2.5 ${
                        msg.isMe ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-800"
                      }`}
                    >
                      <p className="text-sm leading-relaxed">{msg.text}</p>
                      <p className={`text-[10px] mt-1 ${msg.isMe ? "opacity-70" : "text-foreground-400"}`}>
                        {msg.time}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-4 border-t border-background-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSend()}
                    placeholder="Mesaj yaz..."
                    className="flex-1 h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
                  />
                  <button
                    type="button"
                    onClick={handleSend}
                    className="w-10 h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-send-plane-2-line" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <p className="text-sm text-foreground-500">Bir görüşme seç.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
